import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { startActivityTracking, stopActivityTracking, resetActivityTracking } from '../utils/activityTracker';
import { canAccessAdminPath, getRoleHomePath, formatRoleLabel } from '../utils/adminPermissions';
import { getAccessToken, getStoredUser, hydrateSessionFromRefresh, logoutSession } from '../utils/authSession';

function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
    setSidebarOpen(false); // close mobile sidebar on navigation
  }, [location.pathname]);

  const handleLogout = async () => {
    stopActivityTracking();
    await logoutSession({ redirect: false });
    navigate('/admin/login');
  };

  useEffect(() => {
    let isCancelled = false;

    const ensureSessionAndAuthorize = async () => {
      let token = getAccessToken();
      if (!token) {
        const hydrated = await hydrateSessionFromRefresh();
        if (!hydrated || isCancelled) {
          if (!isCancelled) navigate('/admin/login');
          return;
        }
        token = getAccessToken();
      }

      let role = null;
      try {
        role = JSON.parse(localStorage.getItem('user') || '{}')?.role || null;
      } catch (_e) {
        role = null;
      }

      if (!token) {
        navigate('/admin/login');
        return;
      }

      if (!canAccessAdminPath(role, location.pathname)) {
        navigate(getRoleHomePath(role));
        return;
      }

      setUser(getStoredUser());

      // Reset activity tracking on mount (user is active)
      resetActivityTracking();
      // Start activity tracking
      startActivityTracking(handleLogout);
    };

    ensureSessionAndAuthorize();

    return () => {
      isCancelled = true;
      stopActivityTracking();
    };
  }, [navigate, location.pathname]);

  return (
    <div className="admin-layout">
      {!sidebarOpen && (
        <button
          type="button"
          className="admin-sidebar-toggle"
          aria-label="Open admin menu"
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(true)}
        >
          <i className="bi bi-list" aria-hidden="true" />
        </button>
      )}

      {sidebarOpen && (
        <div
          className="admin-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          onTouchStart={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <AdminSidebar
        onLogout={handleLogout}
        show={sidebarOpen}
        onNavigate={() => setSidebarOpen(false)}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="admin-content">
        {user && (
          <div className="admin-welcome-banner" role="status" aria-live="polite">
            <p className="admin-welcome-banner-text mb-0">
              You&apos;re welcome,{' '}
              <strong>{user.full_name || user.username}</strong>
              <span className="admin-welcome-role"> ({formatRoleLabel(user.role)})</span>
            </p>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export default AdminLayout;
